import { Address } from 'APIs/fragments';
import {
  MerchantMarketplaceSettingsResponse,
  MerchantMarketplaceSettingsUpdateRequest,
} from 'APIs/marketplaces/merchant.marketplaces.settings.types';
import { MerchantBusinessType, MerchantIndustry } from 'APIs/merchants/merchants.types';
import { divideBy100, minorCurrencyUnits } from 'utils/currency';

export type MerchantSettingsUpdateFormProps = {
  businessName: string;
  businessType: MerchantBusinessType | '';
  industry: MerchantIndustry | '';
  taxId: string;
  businessDisplayName: string;
  businessWebsite?: string;
  supportEmail: string;
  iconUri: string;
  logoUri: string;
  logoAltText: string;
  firstName: string;
  lastName: string;
  title: string;
  contactEmail: string;
  phoneNo?: string;
  address: Address;
  calculateTaxOnFee: boolean;
  feesTaxExempt: boolean;
  maxPayPlanAmountUnconfirmedCustomers: number;
  maxPayPlanAmountConfirmedCustomers: number;
  minPlanLimit: number;
  paymentProfileType: string;
  feeStructEx?: string;
  feeStructExType?: string;
  feeStruct?: object;
  rules?: Array<object>;
};

type ToMerchantMarketplaceSettingsUpdateRequest = (
  taxCountry: string,
  merchantSettings: MerchantMarketplaceSettingsResponse | null,
  formData: MerchantSettingsUpdateFormProps,
) => MerchantMarketplaceSettingsUpdateRequest;

type ToMerchantSettingsUpdateFormProps = (
  merchantSettings: MerchantMarketplaceSettingsResponse,
) => MerchantSettingsUpdateFormProps;

export const toMerchantMarketplaceSettingsUpdateRequest: ToMerchantMarketplaceSettingsUpdateRequest = (
  taxCountry: string,
  merchantSettings: MerchantMarketplaceSettingsResponse | null,
  formData: MerchantSettingsUpdateFormProps,
): MerchantMarketplaceSettingsUpdateRequest => {
  let feeStructEx = undefined;
  if (typeof formData.feeStructEx === 'string') {
    try {
      feeStructEx = JSON.parse(formData.feeStructEx ?? '');
    } catch (e) {
      throw new Error('Invalid JSON provided for feeStructEx');
    }
  } else {
    feeStructEx = formData.feeStructEx;
  }
  return {
    taxCountry,
    businessContact: {
      contactPerson: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
      },
      contactEmail: formData.contactEmail,
      supportEmail: formData.supportEmail,
      phoneNo: formData.phoneNo ?? '',
      address: ['AU', 'US'].includes(formData.address.country)
        ? { ...formData.address, city: undefined }
        : { ...formData.address, state: undefined },
    },
    businessRegistration: {
      businessName: formData.businessName,
      businessType: formData.businessType === '' ? 'company' : formData.businessType,
      industry: formData.industry === '' ? 'AcRefrigerationRepair' : formData.industry,
      taxId: formData.taxId,
    },
    calculateTaxOnFee: formData.calculateTaxOnFee,
    feesTaxExempt: formData.feesTaxExempt,
    branding: {
      ...merchantSettings?.branding,
      supportEmail: formData.supportEmail,
      businessDisplayName: formData.businessDisplayName,
      businessWebsite: formData.businessWebsite === '' ? undefined : formData.businessWebsite,
      iconUri: formData.iconUri,
      logoUri: formData.logoUri,
      logoAltText: formData.logoAltText,
    },
    customerDashboardBranding: merchantSettings?.customerDashboardBranding,
    emailTemplateSettings: merchantSettings?.emailTemplateSettings,
    payoutSettings: merchantSettings?.payoutSettings,
    customerSettings: merchantSettings?.customerSettings,
    payPlanLimit: {
      maxPayPlanAmountUnconfirmedCustomers: minorCurrencyUnits(formData.maxPayPlanAmountUnconfirmedCustomers),
      maxPayPlanAmountConfirmedCustomers: minorCurrencyUnits(formData.maxPayPlanAmountConfirmedCustomers),
      minPlanLimit: minorCurrencyUnits(formData.minPlanLimit),
    },
    onboardingProfile: {
      paymentProfileType: formData.paymentProfileType === '' ? 'stripeConnectAu' : formData.paymentProfileType,
    },
    feeStructEx,
    feeStruct: formData.feeStruct,
    rules: formData.rules,
  };
};

export const toMerchantSettingsUpdateFormProps: ToMerchantSettingsUpdateFormProps = (
  merchantSettings: MerchantMarketplaceSettingsResponse,
): MerchantSettingsUpdateFormProps => {
  let feeStructEx = undefined;
  try {
    feeStructEx = JSON.stringify(merchantSettings.feeStructEx, null, 2);
  } catch (e) {
    console.log(e);
  }
  return {
    businessName: merchantSettings.businessRegistration.businessName,
    businessType: merchantSettings.businessRegistration.businessType,
    industry: merchantSettings.businessRegistration.industry,
    taxId: merchantSettings.businessRegistration.taxId,
    businessDisplayName: merchantSettings.branding?.businessDisplayName || '',
    businessWebsite: merchantSettings.branding?.businessWebsite || '',
    supportEmail: merchantSettings.branding?.supportEmail || '',
    iconUri: merchantSettings.branding?.iconUri || '',
    logoUri: merchantSettings.branding?.logoUri || '',
    logoAltText: merchantSettings.branding?.logoAltText || '',
    firstName: merchantSettings.businessContact.contactPerson.firstName,
    lastName: merchantSettings.businessContact.contactPerson.lastName,
    title: merchantSettings.businessContact.contactPerson.title,
    contactEmail: merchantSettings.businessContact.contactEmail,
    phoneNo: merchantSettings.businessContact.phoneNo,
    address: merchantSettings.businessContact.address,
    calculateTaxOnFee: merchantSettings.calculateTaxOnFee ?? false,
    feesTaxExempt: merchantSettings.feesTaxExempt ?? false,
    maxPayPlanAmountUnconfirmedCustomers: divideBy100(
      merchantSettings.payPlanLimit.maxPayPlanAmountUnconfirmedCustomers,
    ),
    maxPayPlanAmountConfirmedCustomers: divideBy100(merchantSettings.payPlanLimit.maxPayPlanAmountConfirmedCustomers),
    minPlanLimit: divideBy100(merchantSettings.payPlanLimit.minPlanLimit),
    paymentProfileType: merchantSettings.onboardingProfile.paymentProfileType,
    feeStructEx,
    feeStructExType: 'default_rates',
    feeStruct: merchantSettings.feeStruct,
    rules: merchantSettings.rules,
  };
};
